import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebase';
import { doc, onSnapshot, getDocs, collection, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import LoadingLogo from '../assets/img/app/gohost_meta_logo.png';

// ─── State machine ─────────────────────────────────────────────────────────────
// loading → active       waitlist doc exists, preordering enabled
// loading → seated       waitlist doc doesn't exist (they've been sat down)
// loading → unavailable  restaurant.allowReservationOrders === false
// loading → invalid      bad params / restaurant not found

const WaitlistPreorder = () => {
  const params = new URLSearchParams(window.location.search);
  const restaurantId = params.get('r');
  const waitlistId   = params.get('wl');

  const [status,       setStatus]       = useState('loading');
  const [restaurant,   setRestaurant]   = useState(null);
  const [waitlistData, setWaitlistData] = useState(null);
  const [menus,        setMenus]        = useState([]);
  const [menuItems,    setMenuItems]    = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [preorderItems, setPreorderItems] = useState([]);
  const [showOrderList,  setShowOrderList]  = useState(false);

  // Options modal
  const [selectedItem,    setSelectedItem]    = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showOptions,     setShowOptions]     = useState(false);

  // Delete confirm
  const [deleteIdx,       setDeleteIdx]       = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [authReady, setAuthReady] = useState(false);

  // ── Preorder session timer ──────────────────────────────────────────────────
  // Timer starts on first item add, resets on any cart interaction, expires after 10 min.
  const PREORDER_TIMER_DURATION = 600; // 10 minutes
  const [preorderTimerSeconds, setPreorderTimerSeconds] = useState(null); // null = not started
  const [preorderSessionExpired, setPreorderSessionExpired] = useState(false);
  const preorderTimerStartedRef = useRef(false);

  // Countdown tick — stops when expired or timer is null
  useEffect(() => {
    if (preorderTimerSeconds === null || preorderSessionExpired) return;
    if (preorderTimerSeconds <= 0) {
      setPreorderSessionExpired(true);
      return;
    }
    const id = setTimeout(() => setPreorderTimerSeconds(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [preorderTimerSeconds, preorderSessionExpired]);

  // Start or reset the timer (called on first item add and subsequent cart interactions)
  const resetPreorderTimer = () => {
    preorderTimerStartedRef.current = true;
    setPreorderTimerSeconds(PREORDER_TIMER_DURATION);
    setPreorderSessionExpired(false);
  };

  // Handle session expiry — clear items and return to initial browse state
  const handlePreorderExpiredDismiss = () => {
    setPreorderItems([]);
    setShowOrderList(false);
    setPreorderSessionExpired(false);
    preorderTimerStartedRef.current = false;
    setPreorderTimerSeconds(null);
  };

  // ── Wait for Firebase auth before any Firestore reads ────────────────────
  // firebase.js signs in anonymously but it's async — wait for it to settle
  // so Firestore rules see request.auth != null.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) setAuthReady(true);
    });
    return () => unsub();
  }, []);

  // ── Step 1: fetch restaurant + menu data ──────────────────────────────────
  useEffect(() => {
    if (!authReady) return;
    if (!restaurantId || !waitlistId) {
      setStatus('invalid');
      return;
    }

    const load = async () => {
      try {
        const restSnap = await getDoc(doc(db, 'restaurants_data', restaurantId));
        if (!restSnap.exists()) { setStatus('invalid'); return; }
        const restData = restSnap.data();

        if (!restData.allowReservationOrders) {
          setRestaurant(restData);
          setStatus('unavailable');
          return;
        }

        setRestaurant(restData);
        if (restData.restaurantName) document.title = restData.restaurantName;

        const [menusSnap, itemsSnap] = await Promise.all([
          getDocs(collection(db, 'restaurants_data', restaurantId, 'menus')),
          getDocs(collection(db, 'restaurants_data', restaurantId, 'items')),
        ]);

        const menuList = menusSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const itemList = itemsSnap.docs.map(d => d.data());

        setMenus(menuList);
        setMenuItems(itemList);
        if (menuList.length > 0) setSelectedMenu(menuList[0].menuName);
      } catch (e) {
        console.error('[WaitlistPreorder] load error:', e);
        setStatus('invalid');
      }
    };

    load();
  }, [authReady, restaurantId, waitlistId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 2: subscribe to waitlist doc (only after restaurant loaded) ───────
  useEffect(() => {
    if (!authReady || !restaurantId || !waitlistId || !restaurant) return;

    const unsubscribe = onSnapshot(
      doc(db, 'restaurants_data', restaurantId, 'waitlist', waitlistId),
      async (snap) => {
        if (!snap.exists()) {
          setStatus('seated');
          return;
        }
        const data = snap.data();
        setWaitlistData(data);

        // Load preorder items from the shadow reservation if it exists.
        // Shadow reservation ID is deterministic: 'wl-{waitlistId}'.
        // Fall back to waitlist.preorderItems for entries created before this feature.
        if (preorderItems.length === 0) {
          try {
            const shadowResSnap = await getDoc(
              doc(db, 'restaurants_data', restaurantId, 'reservations', `wl-${waitlistId}`)
            );
            if (shadowResSnap.exists() && shadowResSnap.data().items?.length > 0) {
              setPreorderItems(shadowResSnap.data().items);
            } else if (data.preorderItems?.length > 0) {
              setPreorderItems(data.preorderItems);
            }
          } catch {
            if (data.preorderItems?.length > 0) setPreorderItems(data.preorderItems);
          }
        }

        setStatus('active');
      },
      (e) => {
        console.error('[WaitlistPreorder] snapshot error:', e);
        setStatus('invalid');
      },
    );

    return () => unsubscribe();
  }, [authReady, restaurantId, waitlistId, restaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleOptionChange = (itemName, optionName, selector, isMulti) => {
    setSelectedOptions(prev => {
      const currentItem   = prev[itemName] || {};
      const currentOption = currentItem[optionName] || (isMulti ? [] : '');
      let updated;
      if (isMulti) {
        updated = currentOption.includes(selector.name)
          ? currentOption.filter(n => n !== selector.name)
          : [...currentOption, selector.name];
      } else {
        updated = currentOption === selector.name ? '' : selector.name;
      }
      return { ...prev, [itemName]: { ...currentItem, [optionName]: updated } };
    });
  };

  const handleAddItem = () => {
    if (!selectedItem) return;
    const rawOptions = selectedItem.options || [];
    const selections = selectedOptions[selectedItem.name] || {};

    const formattedOptions = rawOptions.flatMap(opt => {
      const selected = selections[opt.optionName];
      if (!selected) return [{ optionName: opt.optionName, selectedSelector: {} }];
      if (Array.isArray(selected)) {
        return selected.map(sel => ({
          optionName: opt.optionName,
          selectedSelector: {
            name: sel,
            price: opt.selectors?.find(s => s.name === sel)?.price || 0,
          },
        }));
      }
      const found = opt.selectors?.find(s => s.name === selected);
      return [{
        optionName: opt.optionName,
        selectedSelector: { name: selected, price: found?.price || 0 },
      }];
    });

    const finalizedItem = {
      id:   selectedItem.id || selectedItem.name,
      key:  `${selectedItem.id || selectedItem.name}_${Date.now()}`,
      name: selectedItem.name,
      price: parseFloat(selectedItem.price) || 0,
      note: '',
      type: selectedItem.type || '',
      selectedOptions: formattedOptions,
      itemStatus: 'pending',
    };

    setPreorderItems(prev => [...prev, finalizedItem]);
    resetPreorderTimer(); // start timer on first add, reset on subsequent adds
    setShowOptions(false);
    setSelectedItem(null);
    setSelectedOptions({});
  };

  const handleSave = async () => {
    if (!restaurantId || !waitlistId || preorderItems.length === 0) return;
    setSaving(true);
    try {
      // Primary: write items to the shadow reservation so they transfer to the
      // seated reservation automatically when the guest is seated.
      const shadowResRef = doc(db, 'restaurants_data', restaurantId, 'reservations', `wl-${waitlistId}`);
      const shadowResSnap = await getDoc(shadowResRef);
      if (shadowResSnap.exists()) {
        await updateDoc(shadowResRef, { items: preorderItems });
      } else {
        // Fallback for entries created before shadow reservations were introduced.
        await updateDoc(
          doc(db, 'restaurants_data', restaurantId, 'waitlist', waitlistId),
          { preorderItems },
        );
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('[WaitlistPreorder] save failed:', e);
      alert('Could not save your order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const customerName  = waitlistData?.name  || 'Someone';
    const restaurantName = restaurant?.restaurantName || 'the restaurant';
    const text = `${customerName} is inviting you to order ahead at ${restaurantName}. Add your items before you're seated: ${url}`;

    if (navigator.share) {
      try {
        await navigator.share({ text });
      } catch (e) {
        // user cancelled or browser blocked — silent
      }
    } else {
      window.open(`sms:?body=${encodeURIComponent(text)}`);
    }
  };

  const itemTotal = (item) => {
    const optsCost = (item.selectedOptions || []).reduce(
      (s, o) => s + (parseFloat(o.selectedSelector?.price || 0)), 0,
    );
    return item.price + optsCost;
  };

  const orderTotal = preorderItems.reduce((s, i) => s + itemTotal(i), 0);

  // ── Non-active states ─────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1e293b' }}>
        <img src={LoadingLogo} alt="Loading" style={{ width: 56, opacity: 0.9, animation: 'pulse 1.5s infinite' }} />
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔗</div>
          <h2 style={{ fontWeight: 700, fontSize: 20, color: '#111827', marginBottom: 8 }}>Link not found</h2>
          <p style={{ color: '#6b7280', fontSize: 14 }}>This preorder link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🍽️</div>
          <h2 style={{ fontWeight: 700, fontSize: 20, color: '#111827', marginBottom: 8 }}>{restaurant?.restaurantName}</h2>
          <p style={{ color: '#6b7280', fontSize: 14 }}>Online preordering is not available at this restaurant right now.</p>
        </div>
      </div>
    );
  }

  if (status === 'seated') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontWeight: 800, fontSize: 22, color: '#15803d', marginBottom: 10 }}>Your table is ready!</h2>
          <p style={{ color: '#374151', fontSize: 15 }}>
            {preorderItems.length > 0
              ? 'Your pre-ordered items are on their way. Enjoy your meal!'
              : `Welcome to ${restaurant?.restaurantName || 'the restaurant'}. Enjoy your visit!`}
          </p>
        </div>
      </div>
    );
  }

  // ── Active preorder UI ────────────────────────────────────────────────────
  const filteredItems = menuItems.filter(i => i.menu === selectedMenu && !i.outOfStock && !i.isAlcohol);

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#1e293b', padding: '18px 16px 14px', color: 'white' }}>
        <h1 style={{ fontWeight: 800, fontSize: 20, margin: 0 }}>{restaurant?.restaurantName}</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>
          Hi {waitlistData?.name || 'there'}! Add items to your order while you wait.
          {waitlistData?.estimatedWait ? ` · Est. wait: ~${waitlistData.estimatedWait} min` : ''}
        </p>
        <button
          onClick={handleShare}
          style={{
            marginTop: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'transparent',
            border: '1.5px solid #475569',
            borderRadius: 20,
            padding: '5px 14px',
            color: '#94a3b8',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 14 }}>👥</span> Invite friends to order
        </button>
        {preorderItems.length > 0 && saved && (
          <div style={{ marginTop: 8, background: '#166534', borderRadius: 6, padding: '4px 10px', display: 'inline-block', fontSize: 12, fontWeight: 700, color: '#bbf7d0' }}>
            ✓ Order saved
          </div>
        )}
      </div>

      {/* Menu tab bar */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', overflowX: 'auto', display: 'flex', gap: 8, padding: '10px 16px', WebkitOverflowScrolling: 'touch' }}>
        {menus.map(menu => (
          <button
            key={menu.id}
            onClick={() => setSelectedMenu(menu.menuName)}
            style={{
              flexShrink: 0,
              padding: '6px 16px',
              borderRadius: 20,
              border: `1.5px solid #5f799a`,
              background: selectedMenu === menu.menuName ? '#5f799a' : 'transparent',
              color: selectedMenu === menu.menuName ? 'white' : '#5f799a',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {menu.menuName}
          </button>
        ))}
      </div>

      {/* Item grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 12,
        padding: 16,
        paddingBottom: preorderItems.length > 0 ? 96 : 24,
      }}>
        {filteredItems.length === 0 && (
          <p style={{ gridColumn: '1/-1', color: '#9ca3af', textAlign: 'center', padding: 24 }}>
            No items in this menu yet.
          </p>
        )}
        {filteredItems.map((item, idx) => (
          <div
            key={idx}
            onClick={() => { setSelectedItem(item); setSelectedOptions({}); setShowOptions(true); }}
            style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, cursor: 'pointer' }}
          >
            <div style={{ fontWeight: 700, color: '#111827', fontSize: 14, marginBottom: 4 }}>{item.name}</div>
            {item.description && (
              <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 6, lineHeight: 1.4 }}>{item.description}</div>
            )}
            <div style={{ color: '#5f799a', fontWeight: 700, fontSize: 14 }}>${parseFloat(item.price || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>

      {/* Floating order bar */}
      {preorderItems.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'white', borderTop: '1px solid #e5e7eb',
          padding: '12px 16px', display: 'flex', gap: 8, zIndex: 30,
        }}>
          <button
            onClick={() => setShowOrderList(true)}
            style={{ flex: 1, background: '#5f799a', color: 'white', borderRadius: 8, padding: '11px 0', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}
          >
            ({preorderItems.length}) Review · ${orderTotal.toFixed(2)}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || saved}
            style={{ background: saved ? '#16a34a' : '#1e293b', color: 'white', borderRadius: 8, padding: '11px 16px', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', opacity: saving ? 0.7 : 1, flexShrink: 0 }}
          >
            {saving ? '…' : saved ? '✓ Saved' : 'Save Order'}
          </button>
        </div>
      )}

      {/* ── Order list slide-up ─────────────────────────────────────────────── */}
      {showOrderList && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 }}
          onClick={() => setShowOrderList(false)}
        >
          <div
            style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'white', borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '78vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '16px 16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>Your Order</h3>
              <button onClick={() => setShowOrderList(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '8px 16px' }}>
              {preorderItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ flex: 1, marginRight: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</div>
                    {(item.selectedOptions || []).filter(o => o.selectedSelector?.name).map((o, j) => (
                      <div key={j} style={{ fontSize: 12, color: '#6b7280' }}>
                        {o.optionName}: {o.selectedSelector.name}
                        {o.selectedSelector.price ? ` (+$${parseFloat(o.selectedSelector.price).toFixed(2)})` : ''}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>${itemTotal(item).toFixed(2)}</span>
                    <button
                      onClick={() => { setDeleteIdx(i); setShowDeleteModal(true); }}
                      style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 16, cursor: 'pointer', padding: 4 }}
                    >✕</button>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 4px', fontWeight: 700, fontSize: 16 }}>
                <span>Total</span>
                <span>${orderTotal.toFixed(2)}</span>
              </div>
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowOrderList(false)}
                style={{ flex: 1, background: '#f3f4f6', borderRadius: 8, padding: '11px 0', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
              >
                Continue Ordering
              </button>
              <button
                onClick={async () => { await handleSave(); setShowOrderList(false); }}
                disabled={saving}
                style={{ flex: 1, background: '#5f799a', color: 'white', borderRadius: 8, padding: '11px 0', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}
              >
                {saving ? 'Saving…' : 'Save Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Options modal ───────────────────────────────────────────────────── */}
      {showOptions && selectedItem && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}
          onClick={() => { setShowOptions(false); setSelectedItem(null); }}
        >
          <div
            style={{ background: 'white', borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '18px 16px 10px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>{selectedItem.name}</h3>
              {selectedItem.description && (
                <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>{selectedItem.description}</p>
              )}
              <p style={{ margin: '6px 0 0', color: '#5f799a', fontWeight: 700, fontSize: 16 }}>
                ${parseFloat(selectedItem.price || 0).toFixed(2)}
              </p>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: 16 }}>
              {(selectedItem.options || []).map((option, optIdx) => {
                const isMulti  = option.multiSelect;
                const itemKey  = selectedItem.name;
                const current  = selectedOptions[itemKey]?.[option.optionName] || (isMulti ? [] : '');
                return (
                  <div key={optIdx} style={{ marginBottom: 18 }}>
                    <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                      {option.optionName}
                      {option.required && <span style={{ color: '#ef4444' }}> *</span>}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {option.selectors.map((selector, selIdx) => {
                        const isSelected = isMulti
                          ? current.includes(selector.name)
                          : current === selector.name;
                        return (
                          <button
                            key={selIdx}
                            onClick={() => handleOptionChange(itemKey, option.optionName, selector, isMulti)}
                            style={{
                              padding: '7px 14px',
                              borderRadius: 20,
                              border: '1.5px solid #5f799a',
                              background: isSelected ? '#5f799a' : 'white',
                              color: isSelected ? 'white' : '#5f799a',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {selector.name}
                            {selector.price ? ` +$${parseFloat(selector.price).toFixed(2)}` : ''}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {(!selectedItem.options || selectedItem.options.length === 0) && (
                <p style={{ color: '#9ca3af', textAlign: 'center', fontSize: 14 }}>No customisation options for this item.</p>
              )}
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setShowOptions(false); setSelectedItem(null); }}
                style={{ flex: 1, background: '#f3f4f6', borderRadius: 8, padding: '12px 0', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                style={{ flex: 1, background: '#5f799a', color: 'white', borderRadius: 8, padding: '12px 0', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}
              >
                Add to Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ──────────────────────────────────────────────────── */}
      {showDeleteModal && deleteIdx !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center' }}>
            <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 20 }}>
              Remove <strong>{preorderItems[deleteIdx]?.name}</strong>?
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteIdx(null); }}
                style={{ flex: 1, background: '#f3f4f6', borderRadius: 8, padding: '10px 0', fontWeight: 600, border: 'none', cursor: 'pointer' }}
              >
                Keep
              </button>
              <button
                onClick={() => {
                  setPreorderItems(prev => {
                    const updated = prev.filter((_, i) => i !== deleteIdx);
                    if (updated.length === 0) {
                      setShowOrderList(false);
                      // Cart is now empty — stop the timer
                      setPreorderTimerSeconds(null);
                      preorderTimerStartedRef.current = false;
                    } else {
                      resetPreorderTimer(); // reset timer on item removal
                    }
                    return updated;
                  });
                  setShowDeleteModal(false);
                  setDeleteIdx(null);
                }}
                style={{ flex: 1, background: '#ef4444', color: 'white', borderRadius: 8, padding: '10px 0', fontWeight: 700, border: 'none', cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preorder session expired overlay ────────────────────────────────── */}
      {preorderSessionExpired && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.80)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 70, padding: '16px',
        }}>
          <div style={{
            backgroundColor: '#ffffff', borderRadius: '16px',
            padding: '36px 28px', maxWidth: '380px', width: '100%',
            textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          }}>
            <div style={{ fontSize: '52px', marginBottom: '12px' }}>⏰</div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', marginBottom: '10px', lineHeight: '1.3', margin: '0 0 10px' }}>
              Session timed out
            </h2>
            <p style={{ fontSize: '14px', color: '#555555', lineHeight: '1.7', marginBottom: '28px' }}>
              Your preorder session has timed out. Please add your items again.
            </p>
            <button
              onClick={handlePreorderExpiredDismiss}
              style={{
                backgroundColor: '#1e293b', color: '#ffffff',
                border: 'none', borderRadius: '8px',
                padding: '14px 24px', fontSize: '15px', fontWeight: '700',
                cursor: 'pointer', width: '100%',
              }}
            >
              Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaitlistPreorder;
